import {
  HttpClient,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  Observable,
  take,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '@environments/environment';
import {
  AuthInterface,
  CompanyType,
  RefreshTokenInterface,
  TokenInterface,
} from '@core/interfaces/auth.interface';
import { Router } from '@angular/router';
import { CreditInfoInterface } from '@core/interfaces/creditInfo.interface';
import { MENUS } from '@core/constants/menus';
import { Menu } from '@core/interfaces/menu.interface';
import { NotifierService } from '@core/services/notifier.service';
import { PurchaseStateService } from '@app/pages/operation/services/purchase-state.service';
import { DashboardFilterStateService } from '@app/pages/dashboard/services/dashboard-filter-state.service';
import { OperationFilterStateService } from '@app/pages/operation/services/operation-filter-state.service';
import { TokenStorageService } from '@core/auth/services/token-storage.service';
@Injectable({
  providedIn: 'root',
})
export class LoginService {
  authToken = new BehaviorSubject<TokenInterface | null>(null);
  creditInfo?: CreditInfoInterface;
  drawerOpened = new BehaviorSubject<boolean>(false);
  menus = new BehaviorSubject<Menu[]>([]);
  private readonly pendingUrl$ = new BehaviorSubject<string | null>(null);
  private refreshTokenInProgress = false;
  private refreshTokenSubject$ = new BehaviorSubject<RefreshTokenInterface | null>(null);
  private tokenStorageService = inject(TokenStorageService);
  private httpClient = inject(HttpClient);
  private readonly BASE_URL: string = environment.registerUrl;
  private readonly ALLOWED_BUYER_TYPES: CompanyType[] = [
    'FOMENTO',
    'SECURITIZADORA',
    'FIDC',
    'BANK',
    'BUYER',
  ];

  constructor(
    private router: Router,
    private notifier: NotifierService,
    private repurchase: PurchaseStateService,
    private dashboardState: DashboardFilterStateService,
    private operationState: OperationFilterStateService
  ) {
    this.handleAutoAuthenticate();
  }

  auth(data: AuthInterface) {
    const URL = `${this.BASE_URL}/open/login`;

    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: 'Basic ' + btoa(data.userName + ':' + data.password),
      skip: 'true',
    });

    return this.httpClient
      .post<AuthInterface>(URL, null, { headers: headers })
      .pipe(
        tap(({ token, refreshToken }) => {
          if (token && refreshToken) {
            this.handleAuthenticate({ token, refreshToken });
          }
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

   refreshToken(): Observable<RefreshTokenInterface> {
    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject$.pipe(
        filter((token) => token !== null),
        take(1)
      ) as Observable<RefreshTokenInterface>;
    }

    const refreshToken = this.tokenStorageService.getRefreshToken();

    if (!refreshToken) {
      this.router.navigate(['/login']);
      this.tokenStorageService.removeRefreshToken();
      this.tokenStorageService.removeToken();
      return throwError(() => new Error());
    }

    this.refreshTokenInProgress = true;

    return this.httpClient
      .post<RefreshTokenInterface>(
        `${this.BASE_URL}/open/refresh`,
        {},
        {
          headers: { RefreshToken: refreshToken },
        }
      )
      .pipe(
        tap((response) => {
          this.handleAuthenticate(response);
          this.refreshTokenSubject$.next(response);
        }),
        finalize(() => {
          this.refreshTokenInProgress = false;
          this.refreshTokenSubject$.next(null);
        }),
        catchError((error) => {
          this.router.navigate(['/login']);
          this.clearAuthState();
          return throwError(() => error);
        })
      );
  }

  sendPasswordResetEmail(email: string) {
    const URL = `${this.BASE_URL}/open/reset/password`;
    const params = new HttpParams().set('mail', email);
    return this.httpClient.post(URL, params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  }

  updatePassword(hash: string, password: string) {
    const URL = `${this.BASE_URL}/open/update-password`;
    return this.httpClient.put(URL, { hash, password });
  }

  getCreditInfo() {
    const companyId = this.authToken.value?.companyId;
    if (companyId) {
      const URL = `${this.BASE_URL}/secure-api/company/${companyId}/config/monthlyPlan`;
      this.httpClient.get<CreditInfoInterface>(URL).subscribe((creditInfo) => {
        this.creditInfo = {
          ...creditInfo,
          remainingCredit: creditInfo.availableCreditLimit,
        };
      });
    }
  }

  private handleAuthenticate({
    token,
    refreshToken,
  }: {
    token: string;
    refreshToken: string;
  }): void {
    const decodedToken = this.getDecodedToken(token);

    if (!decodedToken || !this.isValidCompanyType(decodedToken?.companyType)) {
      this.showInvalidCompanyError();
      return;
    }
    this.tokenStorageService.setToken(token);
    this.tokenStorageService.setRefreshToken(refreshToken);
    this.authToken.next(decodedToken);
    this.getCreditInfo();
    this.handleRedirect(decodedToken.companyType);
    this.handleSetMenu(decodedToken.companyType);
  }

  private handleSetMenu(companyType: CompanyType): void {
    switch (companyType) {
      case 'LEAD':
        this.menus.next(MENUS.LEAD);
        break;
      case 'FOMENTO':
      case 'SECURITIZADORA':
      case 'FIDC':
      case 'BANK':
      case 'BUYER':
        this.menus.next(MENUS.BUYER);
        break;
    }
  }

  private handleRedirect(companyType: CompanyType): void {
    const isBuyerAllowed = this.ALLOWED_BUYER_TYPES.includes(companyType);
    const isLead = companyType === 'LEAD';

    if (isLead) {
      if (this.pendingUrl) {
        this.router.navigate([this.pendingUrl]);
        this.pendingUrl = null;
      } else {
        this.router.navigate(['/cedente']);
      }
      return;
    }

    if (isBuyerAllowed) {
      if (this.pendingUrl) {
        this.router.navigate([this.pendingUrl]);
        this.pendingUrl = null;
      } else {
        this.router.navigate(['/comprador']);
      }
      return;
    }

    this.router.navigate(['/login']);
  }

  private isValidCompanyType(companyType: CompanyType): boolean {
    return [...this.ALLOWED_BUYER_TYPES, 'LEAD'].includes(companyType);
  }

  private showInvalidCompanyError(): void {
    this.notifier.showNotification({
      messageType: 'errorNotifier',
      message: 'Tipo de empresa nao permitido',
      alternative: 'Contate o time de operacoes',
    });
  }

  logout() {
    this.clearAuthState();
    this.router.navigate(['/login']);
  }

  clearAuthState(): void {
    this.authToken.next(null);
    this.tokenStorageService.removeRefreshToken();
    this.tokenStorageService.removeToken();
    this.drawerOpened.next(false);
    this.repurchase.cleanAll();
    this.dashboardState.clear();
    this.operationState.clear();
  }

  private handleAutoAuthenticate(): void {
    const token = this.tokenStorageService.getToken();
    const refreshToken = this.tokenStorageService.getRefreshToken();
    if (token && refreshToken) {
      this.handleAuthenticate({ token, refreshToken });
    }
  }

  set pendingUrl(url: string | null) {
    this.pendingUrl$.next(url);
  }

  get pendingUrl(): string | null {
    return this.pendingUrl$.getValue();
  }

  getDecodedToken(newToken?: string): TokenInterface | null {
    return this.tokenStorageService.decodeToken(newToken);
  }
}
